import React, { useEffect, useState } from 'react'
import AdsNavbar from '../AdsNavbar'
import Loader from '../../../components/Loader'
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import NotFound from '../../../components/ul/NotFound';
import DeleteButton from '../../../components/ul/DeleteButton';
import generateCaptcha from '../../../components/captcha';
import { formatView } from '../../../components/formatView';

const OpenAds = () => {
    const [loader, setLoader] = useState(false);
    const [nativeAds, setNativeAds] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState(null);
    const [editGroupTitle, setEditGroupTitle] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [newAd, setNewAd] = useState({ a_name: '', a_id: '', a_description: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [group, setGroup] = useState('A');
    const [groups, setGroups] = useState([]);
    const [showGroupManagement, setShowGroupManagement] = useState(false);
    const [newGroup, setNewGroup] = useState({ groupName: '', cloneGroupName: '', groupTitle: '' });

    // Available group names (A-K)
    const availableGroupNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];

    // Get next available group name
    const getNextAvailableGroupName = () => {
        const usedGroups = groups.map(g => g.a_group);
        return availableGroupNames.find(name => !usedGroups.includes(name)) || '';
    };

    // Fetch all groups
    const fetchGroups = async () => {
        try {
            const resp = await Calling.getOpenAdGroups();
            if (resp?.status) {
                setGroups(resp.data);
                // Pre-set the next available group name
                setNewGroup(prev => ({
                    ...prev,
                    groupName: getNextAvailableGroupName()
                }));
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            toast.error('Failed to fetch ad groups');
        }
    };

    // Fetch ads for the selected group
    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getOpenAds(group);
            if (resp?.status) {
                setNativeAds(resp?.data);
                setGroup(resp?.group || group);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchGroups();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [group]);

    // Function to handle search input change
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    }

    // Filtered nativeAds based on searchTerm
    const filteredAds = nativeAds.filter(ad =>
        ad.a_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.a_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.a_description?.toLowerCase().includes(searchTerm.toLowerCase() || '')
    );

    const handleEdit = (id) => {
        setEditingId(id);
    }

    const handleSave = async (id) => {
        const adToUpdate = nativeAds.find(ad => ad._id === id);
        setLoader(true);

        try {
            const resp = await Calling.updateOpenAds(id, {
                a_id: adToUpdate.a_id,
                a_description: adToUpdate?.a_description
            });
            if (resp?.status) {
                toast.success(resp?.message);
                fetchData();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
            setEditingId(null);
        }
    }

    const handleInputChange = (id, field, value) => {
        setNativeAds(nativeAds.map(item =>
            item._id === id ? { ...item, [field]: value } : item
        ));
    }

    const handleAddSubmit = async () => {
        if (newAd.a_name.trim() === '' || newAd.a_id.trim() === '' || newAd.a_description.trim() === '') {
            toast.error('Please fill all the fields');
            return;
        }

        setLoader(true);
        try {
            const selectedGroup = groups.find(g => g.a_group === group);
            const groupTitle = selectedGroup?.a_group_title || '';
            const body = {
                a_name: newAd.a_name.replace(/ /g, ''),
                a_id: newAd.a_id.replace(/ /g, ''),
                a_description: newAd.a_description,
                a_group: group,
                a_group_title: groupTitle // Include the group title directly
            }
            const resp = await Calling.addOpenAds(body);
            if (resp?.status) {
                toast.success(resp?.message);
                setNewAd({ a_name: '', a_id: '', a_description: '' });
                setShowAddForm(false);
                fetchData();
                fetchGroups(); // Update group counts
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    }

    // Group management functions
    const handleCloneGroup = async () => {
        if (!newGroup.groupName.trim() || !newGroup.cloneGroupName.trim() || !newGroup.groupTitle.trim()) {
            toast.error('Please fill all required fields');
            return;
        }

        if (newGroup.groupName === newGroup.cloneGroupName) {
            toast.error('Target group name cannot be the same as source group');
            return;
        }

        // Validate that the group name is one of the allowed names (A-K)
        if (!availableGroupNames.includes(newGroup.groupName)) {
            toast.error('Group name must be a single letter from A to K');
            return;
        }

        setLoader(true);
        try {
            const body = {
                groupName: newGroup.groupName.trim(),
                cloneGroupName: newGroup.cloneGroupName.trim(),
                groupTitle: newGroup.groupTitle.trim()
            }
            const resp = await Calling.cloneOpenAdGroup(body);

            if (resp?.status) {
                toast.success(resp.message);
                setShowGroupForm(false);
                setNewGroup({
                    groupName: '',
                    cloneGroupName: '',
                    groupTitle: ''
                });
                fetchGroups();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error('Error cloning group:', error);
            toast.error('Failed to clone group');
        } finally {
            setLoader(false);
        }
    };

    // Handle edit group title
    const handleEditGroupTitle = (groupName, currentTitle) => {
        setEditingGroupName(groupName);
        setEditGroupTitle(currentTitle || '');
    };

    // Save updated group title
    const handleSaveGroupTitle = async () => {
        if (!editGroupTitle.trim()) {
            toast.error('Group title cannot be empty');
            return;
        }

        setLoader(true);
        try {
            const body = {
                groupName: editingGroupName,
                newTitle: editGroupTitle.trim()
            };

            const resp = await Calling.changeOpenAdGroupTitle(body);

            if (resp?.status) {
                toast.success(resp.message || 'Group title updated successfully');
                setEditingGroupName(null);
                setEditGroupTitle('');
                fetchGroups();
            } else {
                toast.error(resp?.message || 'Failed to update group title');
            }
        } catch (error) {
            console.error('Error updating group title:', error);
            toast.error('Failed to update group title');
        } finally {
            setLoader(false);
        }
    };

    // Cancel group title editing
    const handleCancelGroupTitleEdit = () => {
        setEditingGroupName(null);
        setEditGroupTitle('');
    };

    const handleDeleteGroup = async (groupName) => {
        if (groupName === 'A') {
            toast.error('Default Group (A) cannot be deleted');
            return;
        }

        const content = `You are about to delete the group "${groupName}" and all its ads. To confirm, please solve this math problem.`;
        const isCaptchaValid = generateCaptcha(content);

        if (!isCaptchaValid) {
            return toast.error('Captcha answer is not correct', { autoClose: 2000 });
        }

        setLoader(true);
        try {
            const resp = await Calling.deleteOpenAdGroup({ groupName });

            if (resp?.status) {
                toast.success(resp.message);
                fetchGroups();
                if (group === groupName) {
                    setGroup('A');
                }
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            toast.error('Failed to delete group');
        } finally {
            setLoader(false);
        }
    };

    // Toggle between ad view and group management view
    const toggleGroupManagement = () => {
        setShowGroupManagement(!showGroupManagement);
        // Reset forms when toggling
        setShowAddForm(false);
        setShowGroupForm(false);
        setEditingGroupName(null);
        setEditGroupTitle('');
    };

    // Format dropdown option text to include group name, title and count
    const formatGroupOption = (group) => {
        let title = group.a_group_title || (group.a_group === 'A' ? 'Default' : '');
        return `${group.a_group} (${title}) (${group.adsCount})`;
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='w-full text-main-font mt-16 px-2 md:px-3 md:ml-[4.4rem]'>
                    <h1 className="text-xl sm:text-2xl font-bold mt-4 text-main-font w-auto">Ad Management</h1>
                    <div className='bg-main-white rounded-xl shadow-sm'>
                        <AdsNavbar selectState={'openads'} />
                        <hr className='mt-2 bg-main-border border-[1px]' />

                        {/* Header with toggle button - Enhanced UI */}
                        <div className='px-4 sm:px-6 pt-4 sm:pt-6'>
                            <div className='flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-5'>
                                <div className='flex flex-col sm:flex-row items-center gap-2 mb-3 sm:mb-0'>
                                    <h1 className='text-lg sm:text-2xl font-bold text-main-font'>
                                        {showGroupManagement ? 'Open Ad Groups' : 'Open Ads'}
                                    </h1>
                                    {!showGroupManagement && (
                                        <div className='flex items-center gap-2 ml-0 sm:ml-4'>
                                            <span className='text-sm text-gray-600 font-medium'>Group:</span>
                                            <select
                                                value={group}
                                                onChange={(e) => setGroup(e.target.value)}
                                                className='p-2 border border-main-border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[250px] shadow-sm'
                                            >
                                                {groups.map((g, idx) => (
                                                    <option key={idx} value={g.a_group}>
                                                        {formatGroupOption(g)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                </div>

                                {/* Action Buttons - Improved visual consistency */}
                                <div className='flex gap-3 w-full sm:w-auto justify-between sm:justify-end'>
                                    {/* Conditional "Back to Ads" button when in group management */}
                                    {showGroupManagement ? (
                                        <button
                                            onClick={toggleGroupManagement}
                                            className='px-4 py-2 text-sm sm:text-md text-main-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center'
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            View Ads
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={toggleGroupManagement}
                                                className='px-4 py-2 text-sm sm:text-md text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center'
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                Manage Groups
                                            </button>
                                            <button
                                                onClick={() => setShowAddForm(!showAddForm)}
                                                className={`px-4 py-2 text-sm sm:text-md text-white ${showAddForm ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'} rounded-md transition-colors duration-200 shadow-sm flex items-center`}
                                            >
                                                {showAddForm ? (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                        Cancel Add
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Add Open Ads
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}

                                    {/* Clone Group button in group management mode */}
                                    {showGroupManagement && (
                                        <button
                                            onClick={() => setShowGroupForm(!showGroupForm)}
                                            className={`px-4 py-2 text-sm sm:text-md text-white ${showGroupForm ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'} rounded-md transition-colors duration-200 shadow-sm flex items-center`}
                                        >
                                            {showGroupForm ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Cancel Clone
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    Clone Group
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <hr className='border-main-border mb-4' />

                            {/* Add Ad Form */}
                            {!showGroupManagement && showAddForm && (
                                <div className="mt-2 p-4 sm:p-5 rounded-lg space-y-4 bg-gray-50 shadow-inner border border-gray-200">
                                    <div className="flex items-center mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <h3 className="text-lg font-medium text-gray-700">Add New Ad</h3>
                                    </div>

                                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                        <div className='flex flex-col sm:flex-row items-center'>
                                            <label className='w-full sm:w-1/4 text-sm sm:text-base text-main-font font-medium mb-2 sm:mb-0'>Ad Name:</label>
                                            <input
                                                type="text"
                                                placeholder="Ad Name"
                                                value={newAd.a_name}
                                                onChange={(e) => setNewAd({ ...newAd, a_name: e.target.value })}
                                                className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm'
                                            />
                                        </div>
                                        <div className='flex flex-col sm:flex-row items-center'>
                                            <label className='w-full sm:w-1/4 text-sm sm:text-base text-main-font font-medium mb-2 sm:mb-0'>Ad ID:</label>
                                            <input
                                                type="text"
                                                placeholder="Ad ID"
                                                value={newAd.a_id}
                                                onChange={(e) => setNewAd({ ...newAd, a_id: e.target.value })}
                                                className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm'
                                            />
                                        </div>
                                    </div>

                                    <div className='grid grid-cols-1 gap-2'>
                                        <label className='text-sm sm:text-base text-main-font font-medium'>Description:</label>
                                        <textarea
                                            type="text"
                                            placeholder="Ad Description"
                                            value={newAd?.a_description}
                                            onChange={(e) => setNewAd({ ...newAd, a_description: e.target.value })}
                                            className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm'
                                            rows={3}
                                        />
                                    </div>

                                    <div className='flex justify-start'>
                                        <div className="flex gap-3 sm:gap-4 py-2">
                                            <button
                                                onClick={handleAddSubmit}
                                                className="px-4 py-2 text-sm sm:text-md text-main-white bg-main-blue rounded-md hover:bg-main-blue/80 transition-colors duration-200 shadow-sm flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Submit
                                            </button>
                                            <button
                                                onClick={() => setShowAddForm(false)}
                                                className="px-4 py-2 text-sm sm:text-md text-main-delete border border-main-delete bg-main-white rounded-md hover:bg-main-delete/10 transition-colors duration-200 shadow-sm flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Clone Group Form - Enhanced UI */}
                            {showGroupManagement && showGroupForm && (
                                <div className="mt-2 p-4 sm:p-5 rounded-lg space-y-4 bg-gray-50 shadow-inner border border-gray-200">
                                    <div className="flex items-center mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <h3 className="text-lg font-medium text-gray-700">Clone Existing Ad Group</h3>
                                    </div>

                                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                        <div className='flex flex-col'>
                                            <label className='text-sm sm:text-base text-main-font font-medium mb-2'>
                                                New Group Name <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={newGroup.groupName}
                                                onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })}
                                                className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm appearance-none'
                                                style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                                            >
                                                <option value="">Select a group name</option>
                                                {availableGroupNames.map((name) => {
                                                    // Skip if this group name is already in use
                                                    const isUsed = groups.some(g => g.a_group === name);
                                                    if (isUsed) return null;
                                                    return (
                                                        <option key={name} value={name}>{name}</option>
                                                    );
                                                }).filter(Boolean)}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Group names must be a single letter from A to K
                                            </p>
                                        </div>
                                        <div className='flex flex-col'>
                                            <label className='text-sm sm:text-base text-main-font font-medium mb-2'>
                                                Clone From <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={newGroup.cloneGroupName}
                                                onChange={(e) => setNewGroup({ ...newGroup, cloneGroupName: e.target.value })}
                                                className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm appearance-none'
                                                style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                                            >
                                                <option value="">Select a group</option>
                                                {groups.map((group, index) => (
                                                    <option key={index} value={group.a_group}>
                                                        {formatGroupOption(group)}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500 mt-1">
                                                All ads from this group will be copied to your new group
                                            </p>
                                        </div>
                                    </div>

                                    <div className='grid grid-cols-1 gap-2 mt-2'>
                                        <label className='text-sm sm:text-base text-main-font font-medium mb-2'>
                                            Group Title/Description <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter a title or description for this group"
                                            value={newGroup.groupTitle}
                                            onChange={(e) => setNewGroup({ ...newGroup, groupTitle: e.target.value })}
                                            className='w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-transparent shadow-sm'
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            A descriptive title to help identify the purpose of this group
                                        </p>
                                    </div>

                                    <div className='flex justify-start mt-4'>
                                        <div className="flex gap-3 sm:gap-4 py-2">
                                            <button
                                                onClick={handleCloneGroup}
                                                className="px-4 py-2 text-sm sm:text-md text-main-white bg-main-blue rounded-md hover:bg-main-blue/80 transition-colors duration-200 shadow-sm flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                Clone Group
                                            </button>
                                            <button
                                                onClick={() => setShowGroupForm(false)}
                                                className="px-4 py-2 text-sm sm:text-md text-main-delete border border-main-delete bg-main-white rounded-md hover:bg-main-delete/10 transition-colors duration-200 shadow-sm flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="overflow-x-auto py-4 sm:py-6">
                            {/* Search Bar - Only for Ad View */}
                            {!showGroupManagement && (
                                <div className="mb-4 sm:mb-6 px-4 sm:px-6">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search Ads..."
                                            value={searchTerm}
                                            onChange={handleSearch}
                                            className="pl-10 p-2 border border-gray-300 rounded-md w-full text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500 shadow-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className='overflow-x-auto'>
                                {/* Group Management Table */}
                                {showGroupManagement ? (
                                    <>
                                        {/* Group Management Help Text */}
                                        <div className="px-4 sm:px-6 mb-4">
                                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                                                <div className="flex">
                                                    <div className="flex-shrink-0">
                                                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm text-blue-700">
                                                            Manage your Open Ad Groups here. Group A is the default group and cannot be deleted or renamed. You can edit the title/description for all groups including Group A.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <table className='w-full min-w-[800px]'>
                                            <thead className='bg-main-th text-main-font uppercase'>
                                                <tr>
                                                    <th className="text-left px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium rounded-tl-lg">No</th>
                                                    <th className="text-left px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium">Group Name</th>
                                                    <th className="text-left px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium">Title/Description</th>
                                                    <th className="text-left px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium">Ads Count</th>
                                                    <th className="text-center px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium rounded-tr-lg">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groups.length > 0 ?
                                                    groups.map((g, index) => (
                                                        <tr key={index} className={`hover:bg-main-thHover ${group === g.a_group ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
                                                            <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-main-font">{index + 1}</td>
                                                            <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-700">
                                                                {g.a_group}
                                                                {g.a_group === 'A' && (
                                                                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                                                                {editingGroupName === g.a_group ? (
                                                                    <input
                                                                        type="text"
                                                                        value={editGroupTitle}
                                                                        onChange={(e) => setEditGroupTitle(e.target.value)}
                                                                        className="p-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm shadow-sm"
                                                                        placeholder="Enter group title"
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <span className="text-xs sm:text-sm text-main-font break-all">
                                                                        {g.a_group_title || (g.a_group === 'A' ? 'Default' : 'N/A')}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                    {g.adsCount} ads
                                                                </span>
                                                            </td>
                                                            <td className="text-center px-3 sm:px-4 py-3">
                                                                <div className="flex justify-center gap-2">
                                                                    {editingGroupName === g.a_group ? (
                                                                        <>
                                                                            <button
                                                                                onClick={handleSaveGroupTitle}
                                                                                className="px-3 sm:px-4 py-1 text-xs sm:text-sm text-main-white bg-green-500 hover:bg-green-600 active:bg-green-700 rounded-md transition-colors duration-200 shadow-sm flex items-center"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                                </svg>
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                onClick={handleCancelGroupTitleEdit}
                                                                                className="px-3 sm:px-4 py-1 text-xs sm:text-sm text-main-white bg-red-500 hover:bg-red-600 active:bg-red-700 rounded-md transition-colors duration-200 shadow-sm flex items-center"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                                </svg>
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setGroup(g.a_group);
                                                                                    setShowGroupManagement(false);
                                                                                }}
                                                                                className="px-3 sm:px-4 py-1 text-xs sm:text-sm text-main-white bg-main-blue hover:bg-main-blue/80 active:bg-main-blue/70 rounded-md transition-colors duration-200 shadow-sm flex items-center"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                                </svg>
                                                                                View Ads
                                                                            </button>
                                                                            {/* Edit title button for all groups */}
                                                                            <button
                                                                                onClick={() => handleEditGroupTitle(g.a_group, g.a_group_title)}
                                                                                className="px-3 sm:px-4 py-1 text-xs sm:text-sm text-main-white bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 rounded-md transition-colors duration-200 shadow-sm flex items-center"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                </svg>
                                                                                Edit Title
                                                                            </button>
                                                                            {g.a_group !== 'A' && (
                                                                                <button
                                                                                    onClick={() => handleDeleteGroup(g.a_group)}
                                                                                    className="px-3 sm:px-4 py-1 text-xs sm:text-sm text-white bg-red-500 hover:bg-red-600 active:bg-red-700 rounded-md transition-colors duration-200 shadow-sm flex items-center"
                                                                                >
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                    </svg>
                                                                                    Delete
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                    :
                                                    <tr><td className='text-center' colSpan={5}><NotFound /></td></tr>
                                                }
                                            </tbody>
                                        </table>
                                    </>
                                ) : (
                                    // Ad Management Table
                                    <table className='w-full min-w-[800px]'>
                                        <thead className='bg-main-th text-main-font uppercase'>
                                            <tr>
                                                <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-tl-lg">No</th>
                                                <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">Ad Name</th>
                                                <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">Ad ID</th>
                                                <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">Ad Description</th>
                                                {group === 'A' ?
                                                    ''
                                                    :
                                                    <th className="text-left px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium">Call</th>
                                                }
                                                <th className="text-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-tr-lg">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAds.length > 0 ?
                                                filteredAds?.map((item, index) => (
                                                    <tr key={index} className="hover:bg-main-thHover">
                                                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-main-font">{index + 1}</td>
                                                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-main-font">{item.a_name}</td>
                                                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-main-font">
                                                            {editingId === item._id ? (
                                                                <input
                                                                    type="text"
                                                                    value={item.a_id}
                                                                    onChange={(e) => handleInputChange(item._id, 'a_id', e.target.value)}
                                                                    className="p-2 w-full border border-main-border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm"
                                                                />
                                                            ) : (
                                                                <span className="text-xs sm:text-sm text-main-font break-all">{item.a_id}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm">
                                                            {editingId === item._id ? (
                                                                <textarea
                                                                    type="text"
                                                                    value={item.a_description}
                                                                    onChange={(e) => handleInputChange(item._id, 'a_description', e.target.value)}
                                                                    className="p-2 w-full border border-main-border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm"
                                                                />
                                                            ) : (
                                                                <span className="text-xs sm:text-sm text-main-font break-all">{item.a_description}</span>
                                                            )}
                                                        </td>
                                                        {group === 'A' ?
                                                            ''
                                                            :
                                                            <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm">
                                                                <span className="text-xs sm:text-sm text-main-font break-all text-wrap">{formatView(item.a_count)}</span>
                                                            </td>
                                                        }
                                                        <td className="text-center px-3 sm:px-4 py-2">
                                                            <div className="flex justify-center gap-2">
                                                                <button
                                                                    onClick={() => editingId === item._id ? handleSave(item._id) : handleEdit(item._id)}
                                                                    className={`px-3 sm:px-4 py-1 text-xs sm:text-sm text-main-white rounded transition-colors ${editingId === item._id
                                                                        ? 'bg-green-500 hover:bg-green-600 active:bg-green-700'
                                                                        : 'bg-main-blue hover:bg-main-blue/80 active:bg-main-blue/70'
                                                                        }`}
                                                                >
                                                                    {editingId === item._id ? 'Save' : 'Edit'}
                                                                </button>
                                                                {editingId === item._id && (
                                                                    <button
                                                                        onClick={() => setEditingId(null)}
                                                                        className="px-3 sm:px-4 py-1 text-xs sm:text-sm font-medium text-main-white bg-red-500 hover:bg-red-600 active:bg-red-700 rounded transition-colors"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                                :
                                                <tr><td className='text-center' colSpan={5}><NotFound /></td></tr>
                                            }
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default OpenAds